import React, { useMemo, useCallback } from 'react';
import { PageContent } from '../../../../shared/components/ui';
import { BubbleNavigatorContainer, BubbleNavigator } from '../../../../shared/components/ui/atoms';
import { ResumeFooter, Resume } from '../../../onboarding/components';
import { LoggedInQuestionaryContent } from './LoggedInQuestionaryContent';
import { ONBOARDING_STEPS } from '../../../../utils/constants';
import { FormValues, Ownership, SelectedIndexData } from '../../../../types';
import { useOwnership } from '../../../onboarding/context/OwnershipContext';

interface LoggedInBackgroundContainerProps {
  currentStep: number;
  selectedIndexData: SelectedIndexData;
  setSelectedIndexData: (data: SelectedIndexData) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number, options?: { force?: boolean }) => void;
  values: FormValues;
  setValue: (key: string, value: string | number) => void;
  setMultipleValues: (values: Record<string, any>) => void;
  setBottomValue: (value: number) => void;
  isBrokerMode: boolean;
  isResumeShown: boolean;
  ownerships: Ownership[];
  borrowerData: any;
  setBorrowerData: (data: any) => void;
  isProcessingTeamMode: boolean;
  isBorrowerMode: boolean;
  onFinish: () => void;
}

export const LoggedInBackgroundContainer: React.FC<LoggedInBackgroundContainerProps> = ({
  currentStep,
  selectedIndexData,
  setSelectedIndexData,
  selectedIndex,
  setSelectedIndex,
  values,
  setValue,
  setMultipleValues,
  setBottomValue,
  isBrokerMode,
  isResumeShown,
  ownerships,
  borrowerData,
  setBorrowerData,
  isProcessingTeamMode,
  isBorrowerMode,
  onFinish,
}) => {
  const { ownershipSelected } = useOwnership();
  const isSomeoneElse = ownershipSelected?.isNew === true;
  const shouldSkipPropertyKindStep = false;
  const shouldSkipCreditScoreStep = useMemo(
    () => Boolean(ownershipSelected && ownershipSelected.isNew !== true),
    [ownershipSelected]
  );
  const shouldSkipInvestmentExperienceStep = useMemo(
    () => Boolean(ownershipSelected && ownershipSelected.isNew !== true),
    [ownershipSelected]
  );
  const locationStepIndex = React.useMemo(
    () => ONBOARDING_STEPS.findIndex((step) => step.text === 'Where is the property located?'),
    []
  );
  const propertyKindStepIndex = React.useMemo(
    () => ONBOARDING_STEPS.findIndex((step) => step.text === 'What kind of property is it?'),
    []
  );
  const creditScoreStepIndex = React.useMemo(
    () => ONBOARDING_STEPS.findIndex((step) => step.text === "What’s your credit score?"),
    []
  );
  const investmentExperienceStepIndex = React.useMemo(
    () => ONBOARDING_STEPS.findIndex((step) => step.text === 'How many properties have you purchased/sold in the last 24 months?'),
    []
  );
  const addressBaseIndex = 1; // Address step is always at index 1 (after guarantor step at index 0)
  const hasSelectedInvestmentAddress = typeof values['Investment property address'] === 'string' && values['Investment property address'].trim().length > 0;
  const remainingSteps = useMemo(() => {
    const skipTexts = new Set<string>();
    if (hasSelectedInvestmentAddress && locationStepIndex !== -1) {
      skipTexts.add(ONBOARDING_STEPS[locationStepIndex].text);
    }
    if (shouldSkipPropertyKindStep && propertyKindStepIndex !== -1) {
      skipTexts.add(ONBOARDING_STEPS[propertyKindStepIndex].text);
    }
    if (shouldSkipCreditScoreStep && creditScoreStepIndex !== -1) {
      skipTexts.add(ONBOARDING_STEPS[creditScoreStepIndex].text);
    }
    if (shouldSkipInvestmentExperienceStep && investmentExperienceStepIndex !== -1) {
      skipTexts.add(ONBOARDING_STEPS[investmentExperienceStepIndex].text);
    }
    return ONBOARDING_STEPS.filter((step) => !skipTexts.has(step.text)).length;
  }, [
    creditScoreStepIndex,
    hasSelectedInvestmentAddress,
    investmentExperienceStepIndex,
    locationStepIndex,
    propertyKindStepIndex,
    shouldSkipCreditScoreStep,
    shouldSkipInvestmentExperienceStep,
    shouldSkipPropertyKindStep,
  ]);
  const preAddressStepsCount = addressBaseIndex + 1; // guarantor + address
  // Add 1 for guarantorDetailsStep if isSomeoneElse (it appears after "What kind of property is it?")
  const totalQuestionarySteps = preAddressStepsCount + remainingSteps + (isSomeoneElse ? 1 : 0);
  const lastQuestionaryIndex = totalQuestionarySteps - 1; // inclusive
  const totalBubbles = totalQuestionarySteps + 1; // includes Resume as last bubble
  const resumeStepIndex = lastQuestionaryIndex + 1;

  // Helper function to calculate flow index for a step
  const calculateFlowIndexForStep = useCallback((stepIndexInONBOARDING_STEPS: number): number => {
    let flowIndex = addressBaseIndex + 1 + stepIndexInONBOARDING_STEPS;
    
    // Adjust for skipped steps before this one
    if (hasSelectedInvestmentAddress && locationStepIndex !== -1 && stepIndexInONBOARDING_STEPS > locationStepIndex) {
      flowIndex -= 1;
    }
    if (shouldSkipPropertyKindStep && propertyKindStepIndex !== -1 && stepIndexInONBOARDING_STEPS > propertyKindStepIndex) {
      flowIndex -= 1;
    }
    if (shouldSkipCreditScoreStep && creditScoreStepIndex !== -1 && stepIndexInONBOARDING_STEPS > creditScoreStepIndex) {
      flowIndex -= 1;
    }
    if (shouldSkipInvestmentExperienceStep && investmentExperienceStepIndex !== -1 && stepIndexInONBOARDING_STEPS > investmentExperienceStepIndex) {
      flowIndex -= 1;
    }
    
    // Account for guarantorDetailsStep if isSomeoneElse
    if (isSomeoneElse && propertyKindStepIndex !== -1 && stepIndexInONBOARDING_STEPS > propertyKindStepIndex) {
      flowIndex += 1;
    }
    
    return flowIndex;
  }, [
    addressBaseIndex,
    hasSelectedInvestmentAddress,
    locationStepIndex,
    shouldSkipPropertyKindStep,
    propertyKindStepIndex,
    shouldSkipCreditScoreStep,
    creditScoreStepIndex,
    shouldSkipInvestmentExperienceStep,
    investmentExperienceStepIndex,
    isSomeoneElse
  ]);

  // Calculate the index for guarantorDetailsStep - it should appear after "What kind of property is it?"
  const guarantorDetailsStepIndex = useMemo(() => {
    if (propertyKindStepIndex === -1) {
      return addressBaseIndex + 1;
    }
    return calculateFlowIndexForStep(propertyKindStepIndex) + 1;
  }, [calculateFlowIndexForStep, propertyKindStepIndex, addressBaseIndex]);

  // Helper function to check if a step at a given flow index is completed
  const isStepCompleted = useCallback((flowIndex: number): boolean => {
    // Guarantor step (0) is always considered "available" but not "completed" until ownershipSelected
    if (flowIndex === 0) {
      return Boolean(ownershipSelected);
    }
    
    // Address step (1)
    if (flowIndex === addressBaseIndex) {
      return Boolean(values['__progress_after_address__'] || hasSelectedInvestmentAddress);
    }
    
    // Check if this is the guarantor details step
    if (isSomeoneElse && flowIndex === guarantorDetailsStepIndex) {
      return Boolean(
        values['guarantorFirstName'] !== undefined && 
        values['guarantorFirstName'] !== null && 
        values['guarantorFirstName'] !== '' &&
        values['guarantorLastName'] !== undefined && 
        values['guarantorLastName'] !== null && 
        values['guarantorLastName'] !== ''
      );
    }
    
    // For steps after address, find which ONBOARDING_STEP corresponds to this flow index
    for (let i = 0; i < ONBOARDING_STEPS.length; i++) {
      const step = ONBOARDING_STEPS[i];
      
      // Skip steps that are not in the flow
      if (shouldSkipPropertyKindStep && step.text === ONBOARDING_STEPS[propertyKindStepIndex]?.text) {
        continue;
      }
      if (shouldSkipCreditScoreStep && step.text === ONBOARDING_STEPS[creditScoreStepIndex]?.text) {
        continue;
      }
      if (shouldSkipInvestmentExperienceStep && step.text === ONBOARDING_STEPS[investmentExperienceStepIndex]?.text) {
        continue;
      }
      if (hasSelectedInvestmentAddress && step.text === 'Where is the property located?') {
        continue;
      }
      
      const stepFlowIndex = calculateFlowIndexForStep(i);
      if (stepFlowIndex === flowIndex) {
        return Boolean(values[step.text] !== undefined && values[step.text] !== null && values[step.text] !== '');
      }
    }
    
    return false;
  }, [
    ownershipSelected,
    values,
    hasSelectedInvestmentAddress,
    addressBaseIndex,
    shouldSkipPropertyKindStep,
    propertyKindStepIndex,
    shouldSkipCreditScoreStep,
    creditScoreStepIndex,
    shouldSkipInvestmentExperienceStep,
    investmentExperienceStepIndex,
    calculateFlowIndexForStep,
    isSomeoneElse,
    guarantorDetailsStepIndex
  ]);

  // Calculate the maximum allowed step index based on completed steps
  // This considers the flow structure (guarantor, address, then remaining steps)
  const maxAllowedStepIndex = useMemo(() => {
    // Start with guarantor step (0) - always available
    let maxIndex = 0;
    
    // Check if guarantor is selected (ownershipSelected exists)
    if (ownershipSelected) {
      maxIndex = addressBaseIndex; // Can go to address step (1)
      
      // Check if address is completed
      if (values['__progress_after_address__'] || hasSelectedInvestmentAddress) {
        // Now we need to find the highest step index that has been completed
        // Iterate through ONBOARDING_STEPS and find which ones have values
        let highestCompletedFlowIndex = addressBaseIndex; // Start from address step
        
        // Check if guarantor details step is completed
        let guarantorDetailsCompleted = false;
        if (isSomeoneElse && guarantorDetailsStepIndex !== undefined) {
          guarantorDetailsCompleted = Boolean(
            values['guarantorFirstName'] !== undefined && 
            values['guarantorFirstName'] !== null && 
            values['guarantorFirstName'] !== '' &&
            values['guarantorLastName'] !== undefined && 
            values['guarantorLastName'] !== null && 
            values['guarantorLastName'] !== ''
          );
          if (guarantorDetailsCompleted) {
            highestCompletedFlowIndex = Math.max(highestCompletedFlowIndex, guarantorDetailsStepIndex);
          }
        }
        
        ONBOARDING_STEPS.forEach((step, i) => {
          // Skip steps that are not in the flow
          if (shouldSkipPropertyKindStep && step.text === ONBOARDING_STEPS[propertyKindStepIndex]?.text) {
            return;
          }
          if (shouldSkipCreditScoreStep && step.text === ONBOARDING_STEPS[creditScoreStepIndex]?.text) {
            return;
          }
          if (shouldSkipInvestmentExperienceStep && step.text === ONBOARDING_STEPS[investmentExperienceStepIndex]?.text) {
            return;
          }
          if (hasSelectedInvestmentAddress && step.text === 'Where is the property located?') {
            return;
          }
          
          // Check if this step has a value
          if (values[step.text] !== undefined && values[step.text] !== null && values[step.text] !== '') {
            const flowIndex = calculateFlowIndexForStep(i);
            // If guarantor step is not completed and this step comes after it, don't count it
            // This prevents navigation beyond the guarantor step when it's incomplete
            if (isSomeoneElse && guarantorDetailsStepIndex !== undefined && !guarantorDetailsCompleted && flowIndex > guarantorDetailsStepIndex) {
              return; // Skip this step - it's after an incomplete guarantor step
            }
            highestCompletedFlowIndex = Math.max(highestCompletedFlowIndex, flowIndex);
          }
        });
        
        maxIndex = Math.min(highestCompletedFlowIndex, lastQuestionaryIndex);
      }
    }
    
    // IMPORTANT: maxAllowedStepIndex should be based on completed steps only
    // If user is on a step that is not completed, they should not be able to advance
    // But we need to check this separately in the scroll handler, not here
    // Here we return the last completed step index
    
    // If current step (selectedIndex) is completed and beyond maxIndex, update maxIndex
    // This handles the case where user completes a step and we need to update the max
    if (selectedIndex > maxIndex) {
      const currentStepCompleted = isStepCompleted(selectedIndex);
      if (currentStepCompleted) {
        return Math.min(selectedIndex, lastQuestionaryIndex);
      }
      // If current step is not completed, don't allow going beyond the last completed step
      // Return maxIndex (last completed), not selectedIndex
      // The scroll handler will check if selectedIndex is completed and block accordingly
    }
    
    return maxIndex;
  }, [
    ownershipSelected, 
    values, 
    hasSelectedInvestmentAddress, 
    addressBaseIndex, 
    lastQuestionaryIndex,
    shouldSkipPropertyKindStep,
    shouldSkipCreditScoreStep,
    shouldSkipInvestmentExperienceStep,
    propertyKindStepIndex,
    creditScoreStepIndex,
    investmentExperienceStepIndex,
    locationStepIndex,
    isSomeoneElse,
    calculateFlowIndexForStep,
    selectedIndex,
    isStepCompleted,
    guarantorDetailsStepIndex
  ]);

  // Create a wrapper for setSelectedIndex that blocks navigation to future steps
  const setSelectedIndexWithValidation = React.useCallback((index: number, options?: { force?: boolean; fromSelection?: boolean }) => {
    // Allow force navigation (for internal adjustments)
    if (options?.force) {
      setSelectedIndex(index, options);
      return;
    }
    
    const currentSelectedIndex = selectedIndex;
    const currentStepCompleted = isStepCompleted(currentSelectedIndex);
    
    // If trying to advance forward, check if current step is completed
    if (index > currentSelectedIndex) {
      // Special case: allow advancing to immediate next step (index === currentSelectedIndex + 1)
      // ONLY if it comes from a selection (onSelect) - this handles async value updates
      // For bubbles, we still need to verify the step is completed
      if (index === currentSelectedIndex + 1 && options?.fromSelection) {
        // Allow advancing to immediate next step - user just selected an option
        setSelectedIndex(index, options);
        return;
      }
      
      // If current step is not completed, block advancement
      if (!currentStepCompleted) {
        // Don't allow going to future steps if current step is not completed
        return;
      }
      
      // If current step is completed, check if target index is within allowed range
      // Allow up to maxAllowedStepIndex + 1 (next step after last completed)
      if (index > maxAllowedStepIndex + 1) {
        // Block the navigation - don't allow going to future steps
        return;
      }
    }
    
    // Allow navigation to current or past steps, or next step after last completed (if current is completed)
    setSelectedIndex(index, options);
  }, [maxAllowedStepIndex, setSelectedIndex, selectedIndex, isStepCompleted]);

  // Block navigation to future steps
  React.useEffect(() => {
    // Allow going back (selectedIndex <= maxAllowedStepIndex) or force navigation
    // But block going forward beyond completed steps
    if (selectedIndex > maxAllowedStepIndex + 1) {
      // Allow going to the next step if user just completed current step
      // But block going further
      setSelectedIndex(maxAllowedStepIndex + 1, { force: true });
    }
  }, [selectedIndex, maxAllowedStepIndex, setSelectedIndex]);

  // Track previous isSomeoneElse to detect changes
  const prevIsSomeoneElseRef = React.useRef(isSomeoneElse);
  
  // Adjust selectedIndex when isSomeoneElse changes and new bubbles are added
  React.useEffect(() => {
    // Only adjust if isSomeoneElse changed from false to true (new bubble added)
    if (isSomeoneElse && !prevIsSomeoneElseRef.current) {
      const guarantorDetailsCompleted = guarantorDetailsStepIndex !== undefined ? Boolean(
        values['guarantorFirstName'] !== undefined && 
        values['guarantorFirstName'] !== null && 
        values['guarantorFirstName'] !== '' &&
        values['guarantorLastName'] !== undefined && 
        values['guarantorLastName'] !== null && 
        values['guarantorLastName'] !== ''
      ) : false;
      
      // If guarantor step is already completed, calculate the actual last completed step
      if (guarantorDetailsCompleted) {
        // Calculate the highest completed step index manually (same logic as maxAllowedStepIndex)
        let highestCompletedFlowIndex = addressBaseIndex; // Start from address step
        
        // Include guarantor step since it's completed
        if (guarantorDetailsStepIndex !== undefined) {
          highestCompletedFlowIndex = Math.max(highestCompletedFlowIndex, guarantorDetailsStepIndex);
        }
        
        // Check all ONBOARDING_STEPS to find the highest completed one
        ONBOARDING_STEPS.forEach((step, i) => {
          // Skip steps that are not in the flow
          if (shouldSkipPropertyKindStep && step.text === ONBOARDING_STEPS[propertyKindStepIndex]?.text) {
            return;
          }
          if (shouldSkipCreditScoreStep && step.text === ONBOARDING_STEPS[creditScoreStepIndex]?.text) {
            return;
          }
          if (shouldSkipInvestmentExperienceStep && step.text === ONBOARDING_STEPS[investmentExperienceStepIndex]?.text) {
            return;
          }
          if (hasSelectedInvestmentAddress && step.text === 'Where is the property located?') {
            return;
          }
          
          // Check if this step has a value
          if (values[step.text] !== undefined && values[step.text] !== null && values[step.text] !== '') {
            const flowIndex = calculateFlowIndexForStep(i);
            highestCompletedFlowIndex = Math.max(highestCompletedFlowIndex, flowIndex);
          }
        });
        
        // Determine target index: if all steps are completed, go to Resume, otherwise go to last completed step
        // Use maxAllowedStepIndex if it's higher (it should be recalculated by now)
        const calculatedIndex = Math.min(highestCompletedFlowIndex, lastQuestionaryIndex);
        const allStepsCompleted = calculatedIndex >= lastQuestionaryIndex;
        const targetIndex = allStepsCompleted 
          ? resumeStepIndex 
          : Math.max(calculatedIndex, maxAllowedStepIndex); // Use the higher of the two
        
        // Always set to the target index to ensure bubbles update correctly
        // But ensure we don't go beyond lastQuestionaryIndex unless going to Resume
        const finalIndex = allStepsCompleted 
          ? resumeStepIndex 
          : Math.min(targetIndex, lastQuestionaryIndex);
        
        if (finalIndex >= 0) {
          setSelectedIndex(finalIndex, { force: true });
        }
      } else {
        // If guarantor step is not completed and user is beyond where it should be, adjust
        if (selectedIndex >= totalBubbles) {
          setSelectedIndex(resumeStepIndex, { force: true });
        }
      }
    }
    
    // Update the ref
    prevIsSomeoneElseRef.current = isSomeoneElse;
  }, [isSomeoneElse, totalBubbles, resumeStepIndex, selectedIndex, setSelectedIndex, guarantorDetailsStepIndex, values, lastQuestionaryIndex, addressBaseIndex, shouldSkipPropertyKindStep, propertyKindStepIndex, shouldSkipCreditScoreStep, creditScoreStepIndex, shouldSkipInvestmentExperienceStep, investmentExperienceStepIndex, hasSelectedInvestmentAddress, calculateFlowIndexForStep, maxAllowedStepIndex]);

  // Navigate to guarantor details step when "new guarantor" is selected from DropdownOwnerships (in Resume)
  const prevIsSomeoneElseForNavigationRef = React.useRef(isSomeoneElse);
  React.useEffect(() => {
    // Only navigate if isSomeoneElse becomes true AND we're on Resume step
    // This handles the case when "new guarantor" is selected from DropdownOwnerships in Resume
    // Don't navigate if user is on step 0 (guarantor step) - they should go to address step first
    // Only navigate if guarantorFirstName is missing (user needs to fill it)
    const guarantorFirstName = values?.guarantorFirstName || '';
    if (isSomeoneElse && !prevIsSomeoneElseForNavigationRef.current && selectedIndex === resumeStepIndex && guarantorDetailsStepIndex !== undefined && !guarantorFirstName) {
      setSelectedIndex(guarantorDetailsStepIndex, { force: true });
      // Also update selectedIndexData to trigger scroll
      setSelectedIndexData({ value: guarantorDetailsStepIndex, origin: 'other' });
    }
    prevIsSomeoneElseForNavigationRef.current = isSomeoneElse;
  }, [isSomeoneElse, selectedIndex, resumeStepIndex, guarantorDetailsStepIndex, setSelectedIndex, setSelectedIndexData, values]);
  return (
    <React.Fragment>
      <PageContent
        currentStep={currentStep || 0}
        selectedIndexData={selectedIndexData}
        setSelectedIndexData={setSelectedIndexData}
        isWide={selectedIndex === (lastQuestionaryIndex + 1)}
        maxAllowedStepIndex={maxAllowedStepIndex}
        isStepCompleted={isStepCompleted}
      >
        <LoggedInQuestionaryContent
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndexWithValidation}
          values={values}
          setValue={setValue}
          setBottomValue={setBottomValue}
          isBrokerMode={isBrokerMode}
          ownerships={ownerships}
          resumeStepIndex={lastQuestionaryIndex + 1}
        >
          <Resume
            values={values}
            setValue={setValue}
            setMultipleValues={setMultipleValues}
            isSelected={selectedIndex === (lastQuestionaryIndex + 1)}
            setBottomValue={setBottomValue}
            setSelectedIndex={setSelectedIndexWithValidation}
            setSelectedIndexData={setSelectedIndexData}
            onFinish={onFinish}
            isBrokerMode={isBrokerMode}
            isProcessingTeamMode={isProcessingTeamMode}
            isBorrowerMode={isBorrowerMode}
            setBorrowerData={setBorrowerData}
            borrowerData={borrowerData}
            ownerships={ownerships}
            stepIndex={resumeStepIndex}
          />
        </LoggedInQuestionaryContent>
      </PageContent>

      <BubbleNavigatorContainer>
        <BubbleNavigator
          values={values}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndexWithValidation}
          quantity={totalBubbles}
          maxAllowedStepIndex={maxAllowedStepIndex}
        />
      </BubbleNavigatorContainer>

      <ResumeFooter
        values={values}
        setValue={setValue}
        setSelectedIndex={setSelectedIndexWithValidation}
        setSelectedIndexData={setSelectedIndexData}
        selectedIndex={selectedIndex}
        isHidden={selectedIndex === (lastQuestionaryIndex + 1)}
        hideLastStep={selectedIndex === (lastQuestionaryIndex + 1)}
        isBrokerMode={isBrokerMode}
      />
    </React.Fragment>
  );
}
